import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  cardClass,
  cardHeaderClass,
  cardTitleClass,
  cardBodyClass,
  alertErrorClass,
  spinnerClass,
  badgeClass,
  btnSecondaryClass,
  tableClass,
  tableHeadClass,
  tableHeaderCellClass,
  tableBodyClass,
} from "./dashboardStyles";
import {
  GetPaymentHistoryAction,
  DownloadInvoiceAction,
} from "../../store/action";
import {
  PaymentPurpose,
  PaymentStatus,
  InvoiceStatus,
  PlanNameKeys,
  formatMoney,
} from "../../constants/subscription";

/** The badge colours for each payment outcome. */
const PAYMENT_TONES = {
  [PaymentStatus.PAID]: "bg-brand-50 text-brand-700",
  [PaymentStatus.PENDING]: "bg-ink-100 text-ink-600",
  [PaymentStatus.FAILED]: "bg-red-50 text-red-700",
  [PaymentStatus.CANCELED]: "bg-ink-100 text-ink-600",
  [PaymentStatus.REFUNDED]: "bg-amber-50 text-amber-700",
};

const PAYMENT_LABELS = {
  [PaymentStatus.PAID]: "Dashboard.Billing.status.paid",
  [PaymentStatus.PENDING]: "Dashboard.Billing.status.pending",
  [PaymentStatus.FAILED]: "Dashboard.Billing.status.failed",
  [PaymentStatus.CANCELED]: "Dashboard.Billing.status.canceled",
  [PaymentStatus.REFUNDED]: "Dashboard.Billing.status.refunded",
};

/*
 * The shared responsive table (dashboardStyles) turns each row into a column
 * of "LABEL value" lines below `md`. A payment reads better as a small card —
 * what and how much on top, when and how it went beneath, then its invoice —
 * so the rows and cells here keep the shared desktop look and lay themselves
 * out on a three-column grid on a phone instead.
 */
const rowClass =
  "border-b border-ink-100 transition hover:bg-ink-50 md:last:border-0 max-md:grid max-md:grid-cols-[auto_minmax(0,1fr)_auto] max-md:items-center max-md:gap-x-3 max-md:gap-y-1.5 max-md:rounded-card max-md:border max-md:border-ink-200 max-md:bg-white max-md:p-4 max-md:shadow-card";

const cellBase = "px-3 py-3 align-middle text-fluid-sm text-ink-700 max-md:p-0";

const dateCellClass = `${cellBase} max-md:col-start-1 max-md:row-start-2 max-md:text-ink-500`;

const planCellClass = `${cellBase} max-md:col-span-2 max-md:col-start-1 max-md:row-start-1 max-md:min-w-0 max-md:font-semibold max-md:text-ink-900`;

const amountCellClass = `${cellBase} tabular-nums max-md:col-start-3 max-md:row-start-1 max-md:text-right max-md:font-semibold max-md:text-ink-900`;

const statusCellClass = `${cellBase} max-md:col-span-2 max-md:col-start-2 max-md:row-start-2`;

// "Factură EP0001" on a phone: the word comes from `data-label`, drawn by CSS,
// so it is not a second copy of the column header in the document.
const invoiceCellClass = `${cellBase} max-md:col-span-3 max-md:col-start-1 max-md:row-start-3 max-md:mt-2 max-md:border-t max-md:border-ink-100 max-md:pt-3 max-md:before:mr-1.5 max-md:before:text-ink-500 max-md:before:content-[attr(data-label)]`;

// Empty for every payment without an issued invoice; hidden then, or the grid
// keeps a gap open for a row with nothing in it.
const actionsCellClass = `${cellBase} text-right max-md:col-span-3 max-md:col-start-1 max-md:row-start-4 max-md:mt-1 max-md:empty:hidden`;

/**
 * Every charge this company has been through, and the invoice each produced.
 *
 * Failed and cancelled attempts are listed too, not filtered out. A card that
 * was declined is the thing somebody comes here to check, and a history that
 * silently drops it reads as if the attempt never happened.
 */
const PaymentHistoryPanel = () => {
  const { t, i18n } = useTranslation();

  const [payments, setPayments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Which invoice is being fetched, and what went wrong with the last attempt.
  // Kept per row: a failure on one line must not blank the rest of the table.
  const [downloading, setDownloading] = useState(null);
  const [downloadError, setDownloadError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPayments(await GetPaymentHistoryAction());
    } catch (err) {
      // 403 = no organization yet (mid-onboarding). There is nothing to show
      // and nothing has gone wrong, so it is an empty table, not an error.
      if (err?.response?.status === 403) {
        setPayments([]);
      } else {
        setError(t("Dashboard.Billing.loadError"));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString(i18n.language) : "—";

  /**
   * Fetches the PDF and hands it to the browser to save.
   *
   * An object URL rather than a direct link, because the endpoint needs the
   * Authorization header — an `<a href>` sends no headers, so a plain link
   * would come back 401. The URL is revoked immediately: it only has to survive
   * the click.
   */
  const download = async (payment) => {
    setDownloading(payment.invoiceId);
    setDownloadError(null);
    try {
      const blob = await DownloadInvoiceAction(payment.invoiceId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${payment.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // The body of a failed request is a Blob here (responseType), so there is
      // no message to read out of it — the status is all we have, and 503 is
      // the one worth wording differently because it is worth retrying.
      setDownloadError(
        err?.response?.status === 503
          ? t("Dashboard.Billing.downloadUnavailable")
          : t("Dashboard.Billing.downloadError"),
      );
    } finally {
      setDownloading(null);
    }
  };

  /**
   * The invoice cell. Four outcomes, and the point of keeping them apart is
   * that only one of them is anybody's to act on: a blocked document is waiting
   * on this company's own fiscal details.
   */
  const invoiceCell = (payment) => {
    if (payment.invoiceStatus === InvoiceStatus.ISSUED) {
      return (
        <span className="font-medium tabular-nums text-ink-900">
          {payment.invoiceNumber}
        </span>
      );
    }

    if (payment.invoiceStatus === InvoiceStatus.BLOCKED) {
      return (
        <span className="text-xs text-amber-700">
          {t("Dashboard.Billing.invoiceBlocked")}
        </span>
      );
    }

    if (payment.invoiceStatus === InvoiceStatus.FAILED) {
      return (
        <span className="text-xs text-red-600">
          {t("Dashboard.Billing.invoiceFailed")}
        </span>
      );
    }

    if (payment.invoiceStatus === InvoiceStatus.PENDING) {
      return (
        <span className="text-xs text-ink-500">
          {t("Dashboard.Billing.invoicePending")}
        </span>
      );
    }

    // No invoice row at all — an attempt that never settled owes nothing.
    return <span className="text-ink-400">—</span>;
  };

  return (
    <div className={cardClass}>
      <div className={cardHeaderClass}>
        <h3 className={`${cardTitleClass} flex items-center gap-2`}>
          <i
            className="bi bi-file-earmark-text text-lg text-brand-600"
            aria-hidden="true"
          />
          {t("Dashboard.Billing.historyTitle")}
        </h3>
      </div>
      <div className={cardBodyClass}>
        {error && (
          <div className={alertErrorClass} role="alert">
            <i className="bi bi-exclamation-circle mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {downloadError && (
          <div className={`${alertErrorClass} mb-4`} role="alert">
            <i className="bi bi-exclamation-circle mt-0.5 shrink-0" />
            <span>{downloadError}</span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <div
              className={spinnerClass}
              role="status"
              aria-label={t("Dashboard.Billing.loading")}
            />
          </div>
        ) : !payments?.length ? (
          <p className="m-0 text-fluid-sm text-ink-500">
            {t("Dashboard.Billing.noPayments")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className={tableClass}>
              <thead className={tableHeadClass}>
                <tr>
                  <th className={tableHeaderCellClass}>
                    {t("Dashboard.Billing.columns.date")}
                  </th>
                  <th className={tableHeaderCellClass}>
                    {t("Dashboard.Billing.columns.plan")}
                  </th>
                  <th className={tableHeaderCellClass}>
                    {t("Dashboard.Billing.columns.amount")}
                  </th>
                  <th className={tableHeaderCellClass}>
                    {t("Dashboard.Billing.columns.status")}
                  </th>
                  <th className={tableHeaderCellClass}>
                    {t("Dashboard.Billing.columns.invoice")}
                  </th>
                  <th className={tableHeaderCellClass}>
                    <span className="sr-only">
                      {t("Dashboard.Billing.columns.actions")}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className={tableBodyClass}>
                {payments.map((payment) => (
                  <tr key={payment.id} className={rowClass}>
                    <td className={dateCellClass}>
                      {formatDate(payment.paidAt || payment.createdAt)}
                    </td>
                    <td className={planCellClass}>
                      {payment.purpose === PaymentPurpose.SMS_PACKAGE
                        ? t("Dashboard.Billing.sms.package", {
                            count: payment.smsQuantity,
                          })
                        : t(PlanNameKeys[payment.plan])}
                      {payment.purpose !== PaymentPurpose.SMS_PACKAGE &&
                        payment.periodMonths > 1 && (
                        <span className="ml-1 text-xs text-ink-500">
                          {t("Dashboard.Billing.months", {
                            count: payment.periodMonths,
                          })}
                        </span>
                      )}
                    </td>
                    <td className={amountCellClass}>
                      {formatMoney(payment.amountCents / 100, payment.currency)}
                    </td>
                    <td className={statusCellClass}>
                      <span
                        className={`${badgeClass} ${
                          PAYMENT_TONES[payment.status] ||
                          "bg-ink-100 text-ink-600"
                        }`}
                      >
                        {t(
                          PAYMENT_LABELS[payment.status] ||
                            "Dashboard.Billing.status.pending",
                        )}
                      </span>
                    </td>
                    <td
                      className={invoiceCellClass}
                      data-label={t("Dashboard.Billing.columns.invoice")}
                    >
                      {invoiceCell(payment)}
                    </td>
                    <td className={actionsCellClass}>
                      {payment.invoiceStatus === InvoiceStatus.ISSUED && (
                        <button
                          type="button"
                          className={`${btnSecondaryClass} max-md:min-h-11 max-md:w-full`}
                          disabled={downloading === payment.invoiceId}
                          onClick={() => download(payment)}
                        >
                          <i
                            className={
                              downloading === payment.invoiceId
                                ? "bi bi-arrow-repeat animate-spin"
                                : "bi bi-download"
                            }
                            aria-hidden="true"
                          />
                          <span>{t("Dashboard.Billing.download")}</span>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentHistoryPanel;
